/**
 * contexts/TenantContext.tsx — Per-Meter State & Tariff Context
 * =============================================================
 * Provides the active meter's tariff config and billing engine to all
 * descendant components. Components never import tariff files directly —
 * they read from this context, which means switching meters (even to a
 * different state) automatically re-renders with the correct rules.
 *
 * Usage:
 *   const { tariff, buildBill, stateCode } = useTenant();
 *   const bill = buildBill(rows, 15, 31, meta.contractDemandKva);
 */

import React, { createContext, useContext, useMemo } from 'react';
import type { StateTariff, StateCode } from '@/tariffs';
import { getTariff } from '@/tariffs';
import { getBillingEngine, type BillingEngineFn } from '@/lib';
import type { SheetSource } from '@/config/sheets';

// ─── Context shape ────────────────────────────────────────────────────────────

export interface TenantContextValue {
  /** The active meter's sheet source config. */
  sheet:        SheetSource;
  /** Resolved tariff config for the active meter's state. */
  tariff:       StateTariff;
  /** State code of the active meter. */
  stateCode:    StateCode;
  /**
   * Build a bill breakdown for the active meter.
   * Automatically uses the correct engine + tariff for the state.
   *
   * @param rows         Sanitised telemetry rows for the billing window.
   * @param daysPassed   Days elapsed since billing period start (for projection).
   * @param daysTotal    Total days in the billing month.
   * @param cdKvaOverride  Override contract demand (if different from meta).
   */
  buildBill:    ReturnType<typeof makeBuildBill>;
  /** Convenience: contract demand kVA for this meter. */
  contractDemandKva: number;
}

// ─── Context ──────────────────────────────────────────────────────────────────

const TenantContext = createContext<TenantContextValue | null>(null);

// ─── Helper ───────────────────────────────────────────────────────────────────

function makeBuildBill(engine: BillingEngineFn, tariff: StateTariff, defaultCd: number) {
  return (
    rows:          Parameters<BillingEngineFn>[0],
    daysPassed?:   number,
    daysTotal?:    number,
    cdKvaOverride?: number,
  ) => engine(rows, tariff, daysPassed, daysTotal, cdKvaOverride ?? defaultCd);
}

// ─── Provider ─────────────────────────────────────────────────────────────────

interface TenantProviderProps {
  sheet:    SheetSource;
  children: React.ReactNode;
}

export function TenantProvider({ sheet, children }: TenantProviderProps) {
  const value = useMemo<TenantContextValue>(() => {
    const stateCode = sheet.stateCode;
    const tariff    = getTariff(stateCode);
    const engine    = getBillingEngine(stateCode);
    const cd        = sheet.meta?.contractDemandKva ?? tariff.defaultContractDemandKva;

    return {
      sheet,
      tariff,
      stateCode,
      buildBill:         makeBuildBill(engine, tariff, cd),
      contractDemandKva: cd,
    };
  }, [sheet]);

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Returns the active meter's tariff context.
 * Must be used inside a <TenantProvider>.
 */
export function useTenant(): TenantContextValue {
  const ctx = useContext(TenantContext);
  if (!ctx) {
    throw new Error('[Cortex] useTenant() must be used inside <TenantProvider>.');
  }
  return ctx;
}

/**
 * Returns the active tariff (shorthand for useTenant().tariff).
 * Use this in components that only need tariff constants.
 */
export function useTariff(): StateTariff {
  return useTenant().tariff;
}
